import FileSaver from 'file-saver';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Checkbox,
  FormField,
  HTMLSelect,
  InputGroup,
  majorScale,
} from '.';
import { CompanyThumbnail } from '../components/companies';
import { ContactThumbnail } from '../components/contacts';
import { ICompany, IContact, useStores } from '../stores';
import validateEmail from '../utils/email-validator';
import { defaultColors } from './Colors';
import { ISideSheetProps, SideSheet } from './Dialog';
import { TextArea } from './InputGroup';
import { PDFRenderer } from './PDFRenderer';
import { TagSuggest } from './Suggest';
import { useToast } from './Toaster';

interface ISendValues {
  language: string;
  sender: string;
  recipients: string[];
  addContactsCC: boolean;
  addResponsibleBCC: boolean;
  message?: string;
}

type ContactEmailItemType = {
  company?: ICompany;
  contact?: IContact;
  email: string;
};

export interface ISendItemSideSheetProps
  extends Omit<
    Omit<Omit<ISideSheetProps, 'defaultValue'>, 'actions'>,
    'onSubmit'
  > {
  pdfUrl: string;
  onDownload?: (language?: string) => Promise<any>;
  filename?: () => string;
  onSubmit: (values: ISendValues) => Promise<any | void>;
  defaultValue?: Partial<ISendValues>;
  hasMessage?: boolean;
  sendTitle: string;
  downloadTitle?: string;
  recipientCompany: ICompany;
  canAddCC?: boolean;
  canAddBCC?: boolean;
}

export const SendItemSideSheet: React.FC<ISendItemSideSheetProps> = ({
  pdfUrl,
  onDownload,
  onSubmit,
  defaultValue,
  filename,
  sendTitle,
  downloadTitle,
  recipientCompany,
  canAddCC = true,
  canAddBCC = true,
  hasMessage = false,
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const { authStore, contactStore } = useStores();
  const [downloading, setDownloading] = useState(false);

  const { register, control, errors, watch, formState, handleSubmit } = useForm(
    {
      defaultValues: {
        ...defaultValue,
        language: (
          (defaultValue && defaultValue.language) ||
          navigator.language ||
          'en-uk'
        ).toLowerCase(),
        sender:
          (defaultValue && defaultValue.sender) ||
          (authStore.me && authStore.me.email) ||
          '',
      },
    }
  );

  const language = watch('language', defaultValue?.language);

  const [contacts, setContacts] = useState<IContact[]>();

  useEffect(() => {
    let isSub = true;
    contactStore
      .getAll({
        filter: { company: recipientCompany.id },
      })
      .then((cache) => isSub && setContacts(cache.items));
    return () => {
      isSub = false;
    };
  });

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      onDownload && (await onDownload(language));

      const headers = {
        Authorization: `Token ${authStore.authToken}`,
        'Accept-Language': language,
      };
      const res = await authStore.env.api({
        url: pdfUrl,
        method: 'GET',
        headers,
        responseType: 'blob',
      });
      var file = new File(
        [res.data],
        (filename && filename()) || 'document.pdf',
        {
          type: 'application/pdf;charset=utf-8',
        }
      );
      FileSaver.saveAs(file);
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'PDF file downloaded',
          description: 'Toast message on purchase order pdf download',
        }),
        intent: 'info',
      });
    } catch (err) {
      toaster.showDefaultError(err);
    }
    setDownloading(false);
  };

  const contactItems: ContactEmailItemType[] = contacts
    ? contacts.reduce(
        (p, n) => [
          ...p,
          ...n.emails.map((email) => ({
            contact: n,
            email,
          })),
        ],
        [] as { contact: IContact; email: string }[]
      )
    : ([] as { contact: IContact; email: string }[]);

  if (recipientCompany.email)
    contactItems.push({
      company: recipientCompany,
      email: recipientCompany.email,
    });
  if (recipientCompany.default_att)
    contactItems.push({
      company: recipientCompany,
      email: recipientCompany.default_att,
    });

  return (
    <SideSheet
      {...props}
      actions={
        <>
          <Button
            disabled={formState.isSubmitting}
            iconLeft="cloud-download"
            onClick={() => downloadPdf()}
            isLoading={downloading}
          >
            {downloadTitle || (
              <FormattedMessage
                defaultMessage="Download PDF"
                description="Download button"
              />
            )}
          </Button>
          <Button
            primary
            intent="info"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
            iconLeft="send-message"
          >
            {sendTitle}
          </Button>
        </>
      }
    >
      <Box
        minWidth="360px"
        flexGrow={1}
        borderRight={`2px solid ${defaultColors.gray[100]}`}
        maxHeight="100%"
        overflow="auto"
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <PDFRenderer url={pdfUrl} language={language} />
      </Box>

      <Box
        padding={majorScale(3)}
        maxWidth="470px"
        flexGrow={1}
        width="100%"
        maxHeight="100%"
        overflow="auto"
      >
        <FormField label="Modtager" medium>
          <Controller
            control={control}
            name="recipients"
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Please enter a recipients email address',
                description: 'Input field required validation',
              }),
              validate: (v) =>
                (!validateEmail(v) &&
                  intl.formatMessage({
                    defaultMessage: 'Please enter a valid email address',
                    description: 'Invalid email format',
                  })) ||
                true,
            }}
            render={({ onChange, value, onBlur }) => (
              <TagSuggest
                value={value}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Add recipient',
                })}
                onChange={(v) => {
                  onChange(v);
                }}
                onBlur={onBlur}
                onSelectItem={(item: ContactEmailItemType) => {
                  if ((value || []).indexOf(item.email) === -1) {
                    onChange([...(value || []), item.email]);
                  }
                }}
                isInvalid={!!errors.recipients}
                validationMessage={
                  errors.recipients &&
                  (errors.recipients.filter
                    ? errors.recipients
                        .filter((e) => e && e.message)
                        .map((e) => e!.message)
                        .join('. ')
                    : (errors.recipients as any).message)
                }
                items={contactItems}
                itemRenderer={({
                  contact,
                  company,
                  email,
                }: ContactEmailItemType) => (
                  <>
                    {contact && (
                      <ContactThumbnail
                        contact={contact}
                        showName={false}
                        noLink
                      />
                    )}
                    {company && (
                      <CompanyThumbnail
                        company={company}
                        showName={false}
                        noLink
                      />
                    )}
                    <Box marginLeft={majorScale(1)}>
                      {contact && <p>{contact.name}</p>}
                      {company && <p>{company.name}</p>}
                      <Box className="p muted small">{email}</Box>
                    </Box>
                  </>
                )}
              />
            )}
          />
        </FormField>

        <FormField label="Svar til" medium>
          <InputGroup
            readOnly={true}
            name="sender"
            type="text"
            placeholder="Afsenders email"
            ref={register({ required: 'Du skal angive en afsender' })}
            isInvalid={!!errors.sender}
            validationMessage={errors.sender ? errors.sender.message : ''}
          />
        </FormField>

        <FormField label="Sprog" medium>
          <Controller
            control={control}
            name="language"
            render={({ onChange, value, onBlur }) => (
              <HTMLSelect
                onChange={onChange}
                value={value}
                onBlur={onBlur}
                options={[
                  {
                    value: 'da-dk',
                    label: 'Dansk',
                  },
                  {
                    value: 'en-uk',
                    label: 'Engelsk',
                  },
                ]}
              />
            )}
          />
        </FormField>

        {(canAddCC || canAddBCC) && (
          <FormField label="CC og BCC" medium>
            {canAddCC && (
              <Controller
                control={control}
                name="addContactsCC"
                render={({ onChange, value, onBlur }) => (
                  <Checkbox
                    label="Sæt kontaktpersoner CC"
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                )}
              />
            )}
            {canAddBCC && (
              <Controller
                control={control}
                name="addResponsibleBCC"
                render={({ onChange, value, onBlur }) => (
                  <Checkbox
                    label="Sæt ansvarlige BCC"
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                )}
              />
            )}
          </FormField>
        )}
        {hasMessage && (
          <FormField
            label={intl.formatMessage({
              defaultMessage: 'Message in email',
            })}
            medium
            hint={
              !errors.message &&
              intl.formatMessage({
                defaultMessage:
                  'If you leave the field empty, we will add a default message for you.',
              })
            }
          >
            <TextArea
              name="message"
              ref={register({
                maxLength: {
                  value: 500,
                  message: intl.formatMessage({
                    defaultMessage: 'Message should be 500 characters or less',
                  }),
                },
              })}
              rows={5}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter a message to your customer in the email',
              })}
              maxLength={500}
              isInvalid={!!errors.message}
              validationMessage={errors.message?.message}
            />
          </FormField>
        )}
      </Box>
    </SideSheet>
  );
};
